/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: PhonebookPreviewModal.tsx
 🗓️ Created: 2026-01-16
 🧩 Description:
    Modal component for previewing phonebook XML format with brand selection
    and actual phonebook data (max 3 items per type)
────────────────────────────────────────────────────────────────*/

import { useState, useMemo } from 'react';
import { X, Copy, Check, Phone, Search, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import type { PhoneBrand } from './types';
import { COMPATIBLE_BRANDS } from './CompatibleBrandsBox';

// Contact structure for preview
export interface PreviewContact {
    id: number;
    name: string;
    phoneNumber: string;
    mobileNumber?: string | null;
    organization?: string | null;
    email?: string | null;
    type?: string;
}

interface PhonebookPreviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    phonebookUrl: string;
    contacts: PreviewContact[];
}

// Format functions for each brand
function escapeXml(str: string): string {
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}

function formatYealink(contacts: PreviewContact[]): string {
    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xml += '<YealinkIPPhoneDirectory>\n';
    for (const contact of contacts) {
        xml += '  <DirectoryEntry>\n';
        xml += `    <Name>${escapeXml(contact.name)}</Name>\n`;
        xml += `    <Telephone>${escapeXml(contact.phoneNumber)}</Telephone>\n`;
        if (contact.mobileNumber) {
            xml += `    <Telephone>${escapeXml(contact.mobileNumber)}</Telephone>\n`;
        }
        xml += '  </DirectoryEntry>\n';
    }
    xml += '</YealinkIPPhoneDirectory>';
    return xml;
}

function formatGrandstream(contacts: PreviewContact[]): string {
    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xml += '<AddressBook>\n';
    for (const contact of contacts) {
        xml += '  <Contact>\n';
        const nameParts = contact.name.split(' ');
        xml += `    <FirstName>${escapeXml(nameParts[0] || contact.name)}</FirstName>\n`;
        if (nameParts.length > 1) {
            xml += `    <LastName>${escapeXml(nameParts.slice(1).join(' '))}</LastName>\n`;
        }
        xml += `    <Phone>${escapeXml(contact.phoneNumber)}</Phone>\n`;
        if (contact.mobileNumber) {
            xml += `    <Mobile>${escapeXml(contact.mobileNumber)}</Mobile>\n`;
        }
        if (contact.organization) {
            xml += `    <Company>${escapeXml(contact.organization)}</Company>\n`;
        }
        if (contact.email) {
            xml += `    <Email>${escapeXml(contact.email)}</Email>\n`;
        }
        xml += '  </Contact>\n';
    }
    xml += '</AddressBook>';
    return xml;
}

function formatPolycom(contacts: PreviewContact[]): string {
    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xml += '<directory>\n';
    for (const contact of contacts) {
        xml += '  <item>\n';
        xml += `    <fn>${escapeXml(contact.name)}</fn>\n`;
        xml += `    <tel>${escapeXml(contact.phoneNumber)}</tel>\n`;
        if (contact.mobileNumber) {
            xml += `    <tel type="cell">${escapeXml(contact.mobileNumber)}</tel>\n`;
        }
        if (contact.organization) {
            xml += `    <org>${escapeXml(contact.organization)}</org>\n`;
        }
        if (contact.email) {
            xml += `    <email>${escapeXml(contact.email)}</email>\n`;
        }
        xml += '  </item>\n';
    }
    xml += '</directory>';
    return xml;
}

function formatCisco(contacts: PreviewContact[]): string {
    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xml += '<CiscoIPPhoneDirectory>\n';
    xml += '  <Title>Phonebook</Title>\n';
    xml += '  <Prompt>Select a contact</Prompt>\n';
    for (const contact of contacts) {
        xml += '  <DirectoryEntry>\n';
        xml += `    <Name>${escapeXml(contact.name)}</Name>\n`;
        xml += `    <Telephone>${escapeXml(contact.phoneNumber)}</Telephone>\n`;
        xml += '  </DirectoryEntry>\n';
    }
    xml += '</CiscoIPPhoneDirectory>';
    return xml;
}

function formatSwitchboard(contacts: PreviewContact[]): string {
    const result = {
        contacts: contacts.map(contact => ({
            name: contact.name,
            number: contact.phoneNumber,
            mobile: contact.mobileNumber || null,
            organization: contact.organization || null,
            email: contact.email || null,
            type: contact.type || null
        }))
    };
    return JSON.stringify(result, null, 2);
}

function formatGeneric(contacts: PreviewContact[]): string {
    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xml += '<phonebook>\n';
    for (const contact of contacts) {
        xml += '  <contact>\n';
        xml += `    <name>${escapeXml(contact.name)}</name>\n`;
        xml += `    <phone>${escapeXml(contact.phoneNumber)}</phone>\n`;
        if (contact.mobileNumber) {
            xml += `    <mobile>${escapeXml(contact.mobileNumber)}</mobile>\n`;
        }
        if (contact.organization) {
            xml += `    <organization>${escapeXml(contact.organization)}</organization>\n`;
        }
        if (contact.email) {
            xml += `    <email>${escapeXml(contact.email)}</email>\n`;
        }
        xml += '  </contact>\n';
    }
    xml += '</phonebook>';
    return xml;
}

function getFormatForBrand(brand: PhoneBrand, contacts: PreviewContact[]): string {
    switch (brand) {
        case 'yealink':
            return formatYealink(contacts);
        case 'grandstream':
            return formatGrandstream(contacts);
        case 'polycom':
            return formatPolycom(contacts);
        case 'cisco':
            return formatCisco(contacts);
        case 'switchboard':
            return formatSwitchboard(contacts);
        default:
            return formatGeneric(contacts);
    }
}

export default function PhonebookPreviewModal({
    isOpen,
    onClose,
    phonebookUrl,
    contacts,
}: PhonebookPreviewModalProps) {
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();
    const [copied, setCopied] = useState(false);
    const [urlCopied, setUrlCopied] = useState(false);
    const [selectedBrand, setSelectedBrand] = useState<PhoneBrand>('yealink');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const filteredBrands = useMemo(() => {
        if (!searchQuery.trim()) {
            return COMPATIBLE_BRANDS;
        }
        const query = searchQuery.toLowerCase();
        return COMPATIBLE_BRANDS.filter(({ name }) =>
            name.toLowerCase().includes(query)
        );
    }, [searchQuery]);

    const selectedBrandName = useMemo(() => {
        return COMPATIBLE_BRANDS.find((b) => b.brand === selectedBrand)?.name || selectedBrand;
    }, [selectedBrand]);

    // Build full URL with phone type query parameter
    const fullUrl = useMemo(() => {
        if (!phonebookUrl) return '';
        const separator = phonebookUrl.includes('?') ? '&' : '?';
        return `${phonebookUrl}${separator}type=${selectedBrand}`;
    }, [phonebookUrl, selectedBrand]);

    // Limit to max 3 contacts and format
    const limitedContacts = useMemo(() => {
        return contacts.slice(0, 3);
    }, [contacts]);

    const formattedXml = useMemo(() => {
        if (limitedContacts.length === 0) {
            return t('phonebooks.preview.noContacts', '<!-- No contacts available for preview -->');
        }
        return getFormatForBrand(selectedBrand, limitedContacts);
    }, [selectedBrand, limitedContacts, t]);

    const handleCopyXml = async () => {
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(formattedXml);
            } else {
                // Fallback for older browsers
                const textArea = document.createElement('textarea');
                textArea.value = formattedXml;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
            }
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            showSuccess(t('phonebooks.preview.copiedXml', 'XML copied to clipboard'));
        } catch (error) {
            console.error('Copy failed:', error);
            showError(t('phonebooks.preview.copyError', 'Failed to copy'));
        }
    };

    const handleCopyUrl = async () => {
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(fullUrl);
            } else {
                // Fallback for older browsers
                const textArea = document.createElement('textarea');
                textArea.value = fullUrl;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
            }
            setUrlCopied(true);
            setTimeout(() => setUrlCopied(false), 2000);
            showSuccess(t('phonebooks.preview.copiedUrl', 'URL copied to clipboard'));
        } catch (error) {
            console.error('Copy failed:', error);
            showError(t('phonebooks.preview.copyError', 'Failed to copy'));
        }
    };

    const handleSelectBrand = (brand: PhoneBrand) => {
        setSelectedBrand(brand);
        setIsDropdownOpen(false);
        setSearchQuery('');
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col m-4">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-border">
                    <div>
                        <h2 className="text-xl font-semibold text-foreground">
                            {t('phonebooks.preview.title', 'Phonebook Format Preview')}
                        </h2>
                        <p className="text-sm text-muted-foreground mt-1">
                            {t('phonebooks.preview.description', 'Preview the XML format for different phone brands')}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-muted rounded-md transition-colors"
                    >
                        <X className="h-5 w-5 text-muted-foreground" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-hidden flex flex-col p-6 space-y-3">
                    {/* Brand Selector - inline */}
                    <div className="grid grid-cols-[auto_1fr] gap-4 items-center">
                        <Label className="text-sm whitespace-nowrap">
                            {t('phonebooks.preview.selectBrand', 'Phone Brand')}
                        </Label>
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                className="w-full px-3 py-2 text-left bg-background border border-border rounded-md flex items-center justify-between hover:bg-accent transition-colors"
                            >
                                <div className="flex items-center gap-2">
                                    <Phone className="h-4 w-4 text-muted-foreground" />
                                    <span>{selectedBrandName}</span>
                                </div>
                                {isDropdownOpen ? (
                                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                )}
                            </button>

                            {isDropdownOpen && (
                                <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-hidden">
                                    <div className="p-2 border-b border-border">
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                type="text"
                                                placeholder={t('phonebooks.preview.searchBrand', 'Search brand...')}
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                className="pl-9"
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                    </div>
                                    <div className="max-h-48 overflow-y-auto">
                                        {filteredBrands.map(({ brand, name }) => (
                                            <button
                                                key={brand}
                                                type="button"
                                                onClick={() => handleSelectBrand(brand)}
                                                className={`w-full px-3 py-2 text-left hover:bg-accent flex items-center gap-2 ${selectedBrand === brand ? 'bg-accent' : ''
                                                    }`}
                                            >
                                                <Phone className="h-4 w-4 text-muted-foreground" />
                                                <span className="text-sm">{name}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Full URL with phone type - inline */}
                    <div className="grid grid-cols-[auto_1fr_auto] gap-4 items-center">
                        <Label className="text-sm whitespace-nowrap">
                            {t('phonebooks.preview.fullUrl', 'Full URL')}
                        </Label>
                        <code className="px-3 py-2 bg-muted/50 border border-border rounded-md text-xs font-mono overflow-x-auto whitespace-nowrap">
                            {fullUrl}
                        </code>
                        <button
                            onClick={handleCopyUrl}
                            className={`p-2 rounded-md transition-colors flex-shrink-0 ${urlCopied
                                ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-background border border-border hover:bg-accent'
                                }`}
                        >
                            {urlCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </button>
                    </div>

                    {/* Output Preview - dynamic XML/JSON label */}
                    <div className="flex-1 min-h-0 flex flex-col">
                        <div className="flex items-center justify-between mb-2">
                            <Label className="text-sm">
                                {selectedBrand === 'switchboard'
                                    ? t('phonebooks.preview.jsonOutput', 'JSON Output')
                                    : t('phonebooks.preview.xmlOutput', 'XML Output')}
                            </Label>
                            <button
                                onClick={handleCopyXml}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                            >
                                {copied ? (
                                    <>
                                        <Check className="h-3.5 w-3.5" />
                                        {t('common.copied', 'Copied')}
                                    </>
                                ) : (
                                    <>
                                        <Copy className="h-3.5 w-3.5" />
                                        {selectedBrand === 'switchboard'
                                            ? t('phonebooks.preview.copyJson', 'Copy JSON')
                                            : t('phonebooks.preview.copyXml', 'Copy XML')}
                                    </>
                                )}
                            </button>
                        </div>
                        <div className="flex-1 overflow-auto bg-muted/30 rounded-md border border-border p-4">
                            <pre className="text-xs font-mono text-foreground whitespace-pre-wrap break-words">
                                {formattedXml}
                            </pre>
                        </div>
                    </div>

                    {/* Sample Data Note */}
                    <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-md border border-blue-200 dark:border-blue-900 flex items-start gap-2">
                        <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-blue-900 dark:text-blue-200">
                            <strong>{t('phonebooks.preview.note', 'Note:')}</strong>{' '}
                            {t('phonebooks.preview.sampleDataNote', 'This is a sample preview showing up to 3 contacts from your phonebook. The actual output will include all contacts when accessed by a phone.')}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium bg-background border border-border rounded-md hover:bg-muted transition-colors"
                    >
                        {t('common.close', 'Close')}
                    </button>
                </div>
            </div>
        </div>
    );
}
